/**
 * Parse of POST /api/promotions/evaluate and the existing coupon check payload.
 *
 * Only fields actually present on the response are read. Missing discount,
 * percentage, condition, or free-item fields stay null/undefined and are not
 * invented. Values are not calculated from cart totals.
 */

type JsonMap = Record<string, unknown>

export interface PromotionFreeItem {
  id?: number
  productId?: number
  name: string
  image?: string
  quantity?: number
  originalPrice?: number
}

export interface PromotionBenefit {
  type?: string
  title?: string
  description?: string
  amount?: number
  iconUrl?: string
  freeItems: PromotionFreeItem[]
}

export interface PromotionOffer {
  title?: string
  description?: string
  headline?: string
  condition?: string
  discountText?: string
  discountAmount?: number
  discountPercentage?: number
  finalAmount?: number
  cashbackAmount?: number
  cashbackTitle?: string
  cashbackText?: string
  setItemPriceSavings?: number
  benefits: PromotionBenefit[]
  freeItems: PromotionFreeItem[]
}

export interface PromotionEvaluation {
  hasEligibleOffer: boolean
  title?: string
  subtitle?: string
  headline?: string
  condition?: string
  discountAmount?: number
  discountPercentage?: number
  finalAmount?: number
  offers: PromotionOffer[]
  freeItems: PromotionFreeItem[]
}

export const emptyPromotionEvaluation: PromotionEvaluation = {
  hasEligibleOffer: false,
  offers: [],
  freeItems: [],
}

export function hasPromotionDiscount(evaluation: PromotionEvaluation): boolean {
  return (
    (evaluation.discountAmount != null && evaluation.discountAmount > 0) ||
    (evaluation.discountPercentage != null && evaluation.discountPercentage > 0)
  )
}

export function hasPromotionFreeItems(evaluation: PromotionEvaluation): boolean {
  return evaluation.freeItems.length > 0
}

export function isPromotionOfferDisplayable(offer: PromotionOffer): boolean {
  return (
    (offer.title != null && offer.title.length > 0) ||
    (offer.description != null && offer.description.length > 0) ||
    (offer.headline != null && offer.headline.length > 0) ||
    (offer.condition != null && offer.condition.length > 0) ||
    (offer.discountText != null && offer.discountText.length > 0) ||
    (offer.discountAmount != null && offer.discountAmount > 0) ||
    (offer.discountPercentage != null && offer.discountPercentage > 0) ||
    (offer.cashbackAmount != null && offer.cashbackAmount > 0) ||
    (offer.cashbackText != null && offer.cashbackText.length > 0) ||
    (offer.setItemPriceSavings != null && offer.setItemPriceSavings > 0) ||
    offer.benefits.length > 0 ||
    offer.freeItems.length > 0
  )
}

export function isPromotionBenefitDisplayable(benefit: PromotionBenefit): boolean {
  return (
    (benefit.title != null && benefit.title.length > 0) ||
    (benefit.description != null && benefit.description.length > 0) ||
    (benefit.amount != null && benefit.amount > 0) ||
    benefit.freeItems.length > 0
  )
}

export function promotionFreeItemFromMap(map: JsonMap): PromotionFreeItem {
  const productId = readInt(map, ['product_id', 'productId']) ?? nestedProductId(map)
  const id = readInt(map, ['id', 'item_id', 'itemId']) ?? productId
  const name = itemName(map)
  const originalPrice = itemOriginalPrice(map)
  const quantity = readInt(map, ['quantity', 'qty'])

  if (name == null && productId == null && id == null && originalPrice == null) {
    return { name: '' }
  }

  return {
    id: id ?? undefined,
    productId: productId ?? undefined,
    name: name ?? '',
    image: itemImage(map) ?? undefined,
    quantity: quantity ?? undefined,
    originalPrice: originalPrice ?? undefined,
  }
}

export function promotionOfferFromMap(map: JsonMap): PromotionOffer {
  const nested = nestedOfferMap(map) ?? map
  const discount = readDiscount(map, nested)
  const benefits = readBenefits(map, nested)
  const cashback = readCashback(map, nested)
  const freeItems = uniqueFreeItems([
    ...readFreeItems(map),
    ...readFreeItems(nested),
    ...benefits.flatMap((benefit) => benefit.freeItems),
  ])

  const title = firstDisplayString(map, nested, ['name', 'title', 'label'])
  const description = firstDisplayString(map, nested, [
    'description',
    'descriptiveMessage',
    'descriptive_message',
    'message',
  ])
  const headline = firstDisplayString(map, nested, [
    'headline',
    'successMessage',
    'success_message',
    'greeting',
  ])
  const condition = firstDisplayString(map, nested, [
    'condition',
    'conditionMessage',
    'condition_message',
    'terms',
    'minOrderMessage',
    'min_order_message',
  ])
  const discountText = firstDisplayString(map, nested, [
    'discountText',
    'discount_text',
    'discountDescription',
    'discount_description',
  ])

  return {
    title: title ?? undefined,
    description: description === title ? undefined : (description ?? undefined),
    headline: headline ?? undefined,
    condition: condition ?? undefined,
    discountText: discountText ?? undefined,
    discountAmount: discount.amount,
    discountPercentage: discount.percentage,
    finalAmount:
      readDouble(map, ['final_amount', 'finalAmount']) ??
      readDouble(nested, ['final_amount', 'finalAmount']) ??
      undefined,
    cashbackAmount: cashback.amount,
    cashbackTitle: cashback.title,
    cashbackText: cashback.text,
    setItemPriceSavings: readSetItemPriceSavings(map, nested),
    benefits,
    freeItems,
  }
}

export function promotionEvaluationFromResponse(response: unknown): PromotionEvaluation {
  const roots = promotionRoots(response)
  if (roots.length === 0) return emptyPromotionEvaluation

  if (roots.length === 1 && isExplicitlyIneligible(roots[0]!)) {
    return emptyPromotionEvaluation
  }

  const offers: PromotionOffer[] = []
  for (const root of roots) {
    if (isExplicitlyIneligible(root)) continue
    const offer = promotionOfferFromMap(root)
    if (isPromotionOfferDisplayable(offer)) offers.push(offer)
  }

  if (offers.length === 0) return emptyPromotionEvaluation

  const freeItems = uniqueFreeItems(offers.flatMap((offer) => offer.freeItems))
  const primary = offers[0]!

  return {
    hasEligibleOffer: true,
    title: primary.title,
    subtitle: primary.description,
    headline: primary.headline,
    condition: primary.condition,
    discountAmount: firstAmount(offers.map((offer) => offer.discountAmount)),
    discountPercentage: firstAmount(offers.map((offer) => offer.discountPercentage)),
    finalAmount: firstAmount(offers.map((offer) => offer.finalAmount)),
    offers,
    freeItems,
  }
}

/** Alias matching Dart `PromotionEvaluation.fromResponse`. */
export const fromResponse = promotionEvaluationFromResponse

interface DiscountFields {
  amount?: number
  percentage?: number
}

interface CashbackFields {
  amount?: number
  title?: string
  text?: string
}

const nestedOfferKeys = [
  'promotion',
  'offer',
  'eligiblePromotion',
  'eligible_promotion',
] as const

const promotionListKeys = [
  'promotions',
  'offers',
  'eligiblePromotions',
  'eligible_promotions',
  'appliedPromotions',
  'applied_promotions',
] as const

const benefitListKeys = [
  'benefits',
  'benefit_list',
  'promotionBenefits',
  'promotion_benefits',
] as const

const freeItemKeys = [
  'freeProducts',
  'free_products',
  'freeItems',
  'free_items',
  'freeMeals',
  'free_meals',
  'rewardProducts',
  'reward_products',
  'rewards',
  'gifts',
] as const

function isRecord(value: unknown): value is JsonMap {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function asJsonMap(value: unknown): JsonMap {
  return { ...(value as JsonMap) }
}

function promotionRoots(response: unknown): JsonMap[] {
  const payload = unwrapPayload(response)
  if (payload == null) return []

  const collected: JsonMap[] = []
  const addMaps = (value: unknown) => {
    if (isRecord(value) && Object.keys(value).length > 0) {
      collected.push(asJsonMap(value))
    } else if (Array.isArray(value)) {
      for (const item of value) {
        if (isRecord(item) && Object.keys(item).length > 0) {
          collected.push(asJsonMap(item))
        }
      }
    }
  }

  for (const key of promotionListKeys) {
    if (Object.prototype.hasOwnProperty.call(payload, key)) addMaps(payload[key])
  }

  if (collected.length > 0) return collected

  for (const key of nestedOfferKeys) {
    if (Object.prototype.hasOwnProperty.call(payload, key)) addMaps(payload[key])
  }

  if (collected.length > 0) return collected
  return [payload]
}

function unwrapPayload(response: unknown): JsonMap | null {
  if (Array.isArray(response)) {
    return firstMap(response)
  }
  if (!isRecord(response)) return null

  const root = asJsonMap(response)
  if (!Object.prototype.hasOwnProperty.call(root, 'data')) {
    return Object.keys(root).length === 0 ? null : root
  }

  const data = root['data']
  if (data == null) return null
  if (Array.isArray(data)) {
    if (data.length === 0) return null
    if (data.every((item) => isRecord(item))) {
      return { promotions: data }
    }
    return firstMap(data)
  }
  if (isRecord(data)) {
    const map = asJsonMap(data)
    if (Object.keys(map).length === 0) return null
    // Keep root-level display strings (e.g. descriptiveMessage) visible to the
    // offer parser alongside the nested data fields. Data fields take priority.
    return { ...root, ...map }
  }
  return null
}

function firstMap(items: unknown[]): JsonMap | null {
  for (const item of items) {
    if (isRecord(item) && Object.keys(item).length > 0) {
      return asJsonMap(item)
    }
  }
  return null
}

function nestedOfferMap(payload: JsonMap): JsonMap | null {
  for (const key of nestedOfferKeys) {
    const nested = payload[key]
    if (isRecord(nested) && Object.keys(nested).length > 0) {
      return asJsonMap(nested)
    }
    if (Array.isArray(nested)) {
      return firstMap(nested)
    }
  }
  return null
}

function isExplicitlyIneligible(payload: JsonMap): boolean {
  const flags = [
    'eligible',
    'isEligible',
    'is_eligible',
    'hasEligiblePromotion',
    'has_eligible_promotion',
  ]
  for (const key of flags) {
    if (Object.prototype.hasOwnProperty.call(payload, key) && payload[key] === false) {
      return true
    }
  }
  return false
}

function firstDisplayString(
  payload: JsonMap,
  nested: JsonMap,
  keys: string[],
): string | null {
  return readString(nested, keys) ?? readString(payload, keys)
}

function readString(value: JsonMap, keys: string[]): string | null {
  for (const key of keys) {
    const raw = value[key]
    if (raw == null || isRecord(raw) || Array.isArray(raw)) continue
    const text = String(raw).trim()
    if (text.length === 0 || isStatusMessage(text)) continue
    return text
  }
  return null
}

function isStatusMessage(text: string): boolean {
  const normalized = text.toLowerCase()
  return (
    normalized === 'success' ||
    normalized === 'ok' ||
    normalized === 'error' ||
    normalized === 'apis/messages.success'
  )
}

function readDiscount(payload: JsonMap, nested: JsonMap): DiscountFields {
  const nestedDiscount = nested['discount']
  const payloadDiscount = payload['discount']
  const discountObject = isRecord(nestedDiscount)
    ? asJsonMap(nestedDiscount)
    : isRecord(payloadDiscount)
      ? asJsonMap(payloadDiscount)
      : null

  const percentage =
    readDouble(nested, [
      'discountPercentage',
      'discount_percentage',
      'discountPercent',
      'discount_percent',
      'percentage',
    ]) ??
    readDouble(payload, [
      'discountPercentage',
      'discount_percentage',
      'discountPercent',
      'discount_percent',
      'percentage',
    ]) ??
    (discountObject == null
      ? null
      : readDouble(discountObject, [
          'percentage',
          'percent',
          'discountPercentage',
          'discount_percentage',
        ]))

  const type = (
    readString(nested, ['discountType', 'discount_type', 'type']) ??
    readString(payload, ['discountType', 'discount_type', 'type']) ??
    (discountObject == null
      ? null
      : readString(discountObject, ['type', 'discountType', 'discount_type']))
  )?.toLowerCase()

  const rawAmount =
    readDouble(nested, [
      'discountAmount',
      'discount_amount',
      'discount_value',
      'discountValue',
    ]) ??
    readDouble(payload, [
      'discountAmount',
      'discount_amount',
      'discount_value',
      'discountValue',
    ]) ??
    (discountObject == null
      ? null
      : readDouble(discountObject, ['amount', 'value', 'discount'])) ??
    (!isRecord(nestedDiscount) ? asDouble(nestedDiscount) : null) ??
    (!isRecord(payloadDiscount) ? asDouble(payloadDiscount) : null)

  const isPercentage =
    type === 'percentage' || type === 'percent' || type === 'percentage_discount'

  if (percentage != null && percentage > 0) {
    return {
      amount: isPercentage ? undefined : (rawAmount ?? undefined),
      percentage,
    }
  }

  if (isPercentage && rawAmount != null && rawAmount > 0) {
    return { percentage: rawAmount }
  }

  if (rawAmount != null && rawAmount > 0) {
    return { amount: rawAmount }
  }

  return {}
}

function readCashback(payload: JsonMap, nested: JsonMap): CashbackFields {
  const nestedCashback = nested['cashback']
  const payloadCashback = payload['cashback']
  const cashbackObject = isRecord(nestedCashback)
    ? asJsonMap(nestedCashback)
    : isRecord(payloadCashback)
      ? asJsonMap(payloadCashback)
      : null

  const amount =
    readDouble(nested, [
      'cashbackAmount',
      'cashback_amount',
      'cashbackValue',
      'cashback_value',
      'walletCredit',
      'wallet_credit',
    ]) ??
    readDouble(payload, [
      'cashbackAmount',
      'cashback_amount',
      'cashbackValue',
      'cashback_value',
      'walletCredit',
      'wallet_credit',
    ]) ??
    (cashbackObject == null
      ? null
      : readDouble(cashbackObject, ['amount', 'value', 'cashback'])) ??
    (!isRecord(nestedCashback) ? asDouble(nestedCashback) : null) ??
    (!isRecord(payloadCashback) ? asDouble(payloadCashback) : null)

  const title =
    readString(cashbackObject ?? {}, ['title', 'name', 'label']) ??
    firstDisplayString(payload, nested, ['cashbackTitle', 'cashback_title'])
  const text =
    readString(cashbackObject ?? {}, ['description', 'message', 'text']) ??
    firstDisplayString(payload, nested, [
      'cashbackText',
      'cashback_text',
      'cashbackDescription',
      'cashback_description',
      'cashbackMessage',
      'cashback_message',
    ])

  if (
    (amount == null || amount <= 0) &&
    (text == null || text.length === 0) &&
    (title == null || title.length === 0)
  ) {
    return {}
  }

  return {
    amount: amount != null && amount > 0 ? amount : undefined,
    title: title ?? undefined,
    text: text === title ? undefined : (text ?? undefined),
  }
}

function readSetItemPriceSavings(payload: JsonMap, nested: JsonMap): number | undefined {
  const value =
    readDouble(nested, [
      'set_item_price_savings',
      'setItemPriceSavings',
      'setItemPriceSaving',
    ]) ??
    readDouble(payload, [
      'set_item_price_savings',
      'setItemPriceSavings',
      'setItemPriceSaving',
    ])
  if (value == null || value <= 0) return undefined
  return value
}

function readBenefits(payload: JsonMap, nested: JsonMap): PromotionBenefit[] {
  const items: PromotionBenefit[] = []
  for (const key of benefitListKeys) {
    items.push(...benefitsFrom(nested[key]))
    if (payload !== nested) {
      items.push(...benefitsFrom(payload[key]))
    }
  }
  return uniqueBenefits(items)
}

function benefitsFrom(value: unknown): PromotionBenefit[] {
  if (!Array.isArray(value)) return []

  const items: PromotionBenefit[] = []
  for (const item of value) {
    if (!isRecord(item)) continue
    const parsed = benefitFromMap(asJsonMap(item))
    if (isPromotionBenefitDisplayable(parsed)) items.push(parsed)
  }
  return items
}

function benefitFromMap(map: JsonMap): PromotionBenefit {
  const type = readString(map, ['type', 'benefitType', 'benefit_type', 'kind'])
  const title = readString(map, ['title', 'name', 'label'])
  const description = readString(map, [
    'description',
    'descriptiveMessage',
    'descriptive_message',
    'message',
  ])
  const icon = readString(map, ['icon', 'iconUrl', 'icon_url'])
  const amount = readDouble(map, [
    'amount',
    'value',
    'discountAmount',
    'discount_amount',
    'cashbackAmount',
    'cashback_amount',
  ])

  const rewardItems = isFreeBenefit(type)
    ? [...freeItemsFrom(map['products']), ...freeItemsFrom(map['items'])]
    : []

  return {
    type: type ?? undefined,
    title: title ?? undefined,
    description: description === title ? undefined : (description ?? undefined),
    amount: amount != null && amount > 0 ? amount : undefined,
    iconUrl: icon != null && icon.startsWith('http') ? icon : undefined,
    freeItems: uniqueFreeItems([...readFreeItems(map), ...rewardItems]),
  }
}

function isFreeBenefit(type: string | null): boolean {
  if (type == null || type.length === 0) return false
  const normalized = type.toLowerCase()
  return (
    normalized.includes('free') ||
    normalized.includes('gift') ||
    normalized.includes('product')
  )
}

function uniqueBenefits(items: PromotionBenefit[]): PromotionBenefit[] {
  const seen = new Set<string>()
  const unique: PromotionBenefit[] = []
  for (const item of items) {
    const key = `${item.type ?? ''}|${item.title ?? ''}|${item.description ?? ''}|${item.amount ?? ''}`
    if (!seen.has(key)) {
      seen.add(key)
      unique.push(item)
    }
  }
  return unique
}

function readFreeItems(map: JsonMap): PromotionFreeItem[] {
  const items: PromotionFreeItem[] = []

  for (const key of freeItemKeys) {
    items.push(...freeItemsFrom(map[key]))
  }

  const flagged = map['items'] ?? map['products'] ?? map['meals']
  if (Array.isArray(flagged)) {
    for (const item of flagged) {
      if (!isRecord(item)) continue
      const itemMap = asJsonMap(item)
      if (!isMarkedFree(itemMap)) continue
      const parsed = promotionFreeItemFromMap(itemMap)
      if (parsed.name.length > 0) items.push(parsed)
    }
  }

  return items
}

function freeItemsFrom(value: unknown): PromotionFreeItem[] {
  if (isRecord(value)) {
    const parsed = promotionFreeItemFromMap(asJsonMap(value))
    return isUsableFreeItem(parsed) ? [parsed] : []
  }
  if (!Array.isArray(value)) return []

  const items: PromotionFreeItem[] = []
  for (const item of value) {
    if (!isRecord(item)) continue
    const parsed = promotionFreeItemFromMap(asJsonMap(item))
    if (isUsableFreeItem(parsed)) items.push(parsed)
  }
  return items
}

function isUsableFreeItem(item: PromotionFreeItem): boolean {
  return (
    item.name.length > 0 ||
    item.productId != null ||
    item.id != null ||
    (item.originalPrice != null && item.originalPrice > 0)
  )
}

function isMarkedFree(item: JsonMap): boolean {
  const flags = ['isFree', 'is_free', 'free', 'is_gift', 'isGift']
  for (const key of flags) {
    const value = item[key]
    if (value === true || value === 1 || value === '1' || value === 'true') return true
  }
  return false
}

function itemName(map: JsonMap): string | null {
  const direct = readString(map, ['name', 'title', 'label'])
  if (direct != null) return direct

  const product = map['product']
  if (isRecord(product)) {
    const productName = readString(asJsonMap(product), ['name', 'title'])
    if (productName != null) return productName
  }

  const meal = map['meal']
  if (isRecord(meal)) {
    const mealMap = asJsonMap(meal)
    const mealName = readString(mealMap, ['name', 'title'])
    const nestedProduct = mealMap['product']
    if (isRecord(nestedProduct)) {
      const productName = readString(asJsonMap(nestedProduct), ['name', 'title'])
      if (productName != null) return productName
    }
    return mealName
  }

  return null
}

function nestedProductId(map: JsonMap): number | null {
  const product = map['product']
  if (isRecord(product)) {
    return readInt(asJsonMap(product), ['id'])
  }
  const meal = map['meal']
  if (isRecord(meal)) {
    const mealMap = asJsonMap(meal)
    const mealProduct = mealMap['product']
    if (isRecord(mealProduct)) {
      return readInt(asJsonMap(mealProduct), ['id'])
    }
    return readInt(mealMap, ['id'])
  }
  return null
}

function itemOriginalPrice(map: JsonMap): number | null {
  const keys = [
    'original_price',
    'originalPrice',
    'old_price',
    'oldPrice',
    'compare_at_price',
    'compareAtPrice',
    'menu_value',
    'menuValue',
  ]

  const direct = readDouble(map, keys)
  if (direct != null && direct > 0) return direct

  const product = map['product']
  if (isRecord(product)) {
    const productMap = asJsonMap(product)
    const productPrice = readDouble(productMap, keys) ?? positivePrice(productMap)
    if (productPrice != null) return productPrice
  }

  const meal = map['meal']
  if (isRecord(meal)) {
    const mealMap = asJsonMap(meal)
    const mealPrice = readDouble(mealMap, keys)
    if (mealPrice != null && mealPrice > 0) return mealPrice
    const nestedProduct = mealMap['product']
    if (isRecord(nestedProduct)) {
      const nestedMap = asJsonMap(nestedProduct)
      return readDouble(nestedMap, keys) ?? positivePrice(nestedMap)
    }
  }

  return null
}

function positivePrice(map: JsonMap): number | null {
  const price = readDouble(map, ['price'])
  if (price == null || price <= 0) return null
  return price
}

function itemImage(map: JsonMap): string | null {
  const direct = readString(map, ['image', 'imageUrl', 'image_url', 'photo'])
  if (direct != null) return direct

  const product = map['product']
  if (isRecord(product)) {
    const productImage = readString(asJsonMap(product), [
      'image',
      'imageUrl',
      'image_url',
    ])
    if (productImage != null) return productImage
  }

  const meal = map['meal']
  if (isRecord(meal)) {
    return readString(asJsonMap(meal), ['image', 'imageUrl', 'image_url'])
  }

  return null
}

function uniqueFreeItems(items: Iterable<PromotionFreeItem>): PromotionFreeItem[] {
  const seen = new Set<string>()
  const unique: PromotionFreeItem[] = []
  for (const item of items) {
    if (!isUsableFreeItem(item)) continue
    const key = `${item.productId ?? ''}|${item.id ?? ''}|${item.name}|${item.quantity ?? ''}|${item.originalPrice ?? ''}`
    if (!seen.has(key)) {
      seen.add(key)
      unique.push(item)
    }
  }
  return unique
}

function firstAmount(values: Iterable<number | undefined>): number | undefined {
  for (const value of values) {
    if (value != null && value > 0) return value
  }
  return undefined
}

function readDouble(map: JsonMap, keys: string[]): number | null {
  for (const key of keys) {
    if (!Object.prototype.hasOwnProperty.call(map, key)) continue
    const value = asDouble(map[key])
    if (value != null) return value
  }
  return null
}

function readInt(map: JsonMap, keys: string[]): number | null {
  for (const key of keys) {
    if (!Object.prototype.hasOwnProperty.call(map, key)) continue
    const raw = map[key]
    if (typeof raw === 'number' && Number.isInteger(raw)) return raw
    const parsed = Number.parseInt(raw == null ? '' : String(raw), 10)
    return Number.isNaN(parsed) ? null : parsed
  }
  return null
}

function asDouble(raw: unknown): number | null {
  if (raw == null || isRecord(raw) || Array.isArray(raw)) return null
  if (typeof raw === 'number' && !Number.isNaN(raw)) return raw
  if (typeof raw === 'boolean') return null
  const parsed = Number.parseFloat(String(raw))
  return Number.isNaN(parsed) ? null : parsed
}

/** Namespace-style factories matching Dart naming. */
export const PromotionFreeItem = {
  fromMap: promotionFreeItemFromMap,
}

export const PromotionOffer = {
  fromMap: promotionOfferFromMap,
}

export const PromotionEvaluation = {
  empty: emptyPromotionEvaluation,
  fromResponse: promotionEvaluationFromResponse,
}
